<template>
    <div>
        <Button
            v-if="!target"
            :id="`popconfirmButton${id}`"
            :data-cy="`${id}Button`"
            :disabled="disabled"
            variant="destruction"
        >
            <slot>
                {{ buttonText }}
            </slot>
        </Button>

        <b-popover
            :id="`popconfirm${id}`"
            ref="popconfirm"
            :data-cy="`${id}Popconfirm`"
            :disabled="disabled"
            :placement="placement"
            :target="targetOrDefault"
            :triggers="triggers"
            custom-class="popconfirm"
        >
            <div
                class="popconfirm-body"
                data-cy="popconfirmBody"
            >
                <div class="d-flex">
                    <FontAwesomeIcon
                        class="mr-3"
                        icon="exclamation-circle"
                        size="lg"
                    />

                    <div>
                        <div class="title-row">
                            <span
                                class="popconfirm-title"
                                data-cy="popconfirmTitle"
                            >
                                <slot name="title">
                                    {{ title }}
                                </slot>
                            </span>
                        </div>

                        <slot name="detail" />
                    </div>
                </div>

                <div class="button-row">
                    <Button
                        variant="tertiary"
                        data-cy="cancelButton"
                        @click="cancel"
                    >
                        {{ cancelButtonText }}
                    </Button>

                    <Button
                        variant="destruction"
                        data-cy="confirmButton"
                        @click="confirm"
                    >
                        {{ confirmButtonText }}
                    </Button>
                </div>
            </div>
        </b-popover>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {faExclamationCircle} from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
    import uniqueId from 'lodash/uniqueId';

    import Button from './Button.vue';

    library.add(faExclamationCircle);

    /**
     * @group Buttons
     * A component that utilizes bootstrap popover for button confirmation
     */
    export default {
        name: 'PopConfirm',
        components: {
            Button,
            FontAwesomeIcon,
        },
        props: {
            /**
             * A string indicating where the popconfirm should appear with respect to the parent button. Defaults to top
             * Valid values: [top, bottom, left, right, topleft, topright, bottomleft, bottomright, lefttop, leftbottom, righttop, rightbottom]
             */
            placement: {
                type: String,
                // `'top'`
                default: 'top',
            },
            /**
             * Sets the title of the popover.
             */
            title: {
                type: String,
                // `'Are you sure you want to delete?'`
                default: 'Are you sure you want to delete?',
            },
            /**
             * Sets the text of confirmation button.
             */
            confirmButtonText: {
                type: String,
                // `'Yes'`
                default: 'Yes',
            },
            /**
             * Sets the text of cancel button.
             */
            cancelButtonText: {
                type: String,
                // `'Cancel'`
                default: 'Cancel',
            },
            /**
             * Sets custom classes on the PopConfirm component.
             */
            customClass: {
                type: String,
                // `null`
                default: null,
            },
            /**
             * Sets id on the PopConfirm component.
             */
            id: {
                type: String,
                // `uniqueId()`
                default: () => {
                    return uniqueId();
                },
            },
            /**
             * Disables PopConfirm component.
             */
            disabled: {
                type: Boolean,
                // `false`
                default: false,
            },
            /**
             * The id of the button that the PopConfirm should be attached to. If not supplied, PopConfirm will render its own button.
             */
            target: {
                type: String,
                // `''`
                default: '',
            },
            /**
             * The type of trigger(s) used to render the PopConfirm. If not supplied, PopConfirm will render on click.
             */
            triggers: {
                type: String,
                // `'click'`
                default: 'click',
            },
            /**
             * The text of the button that the PopConfirm renders if target is not set.
             */
            buttonText: {
                type: String,
                // `'Remove'`
                default: 'Remove',
            },
        },
        computed: {
            targetOrDefault() {
                if (this.target === '') {
                    return `popconfirmButton${this.id}`;
                } else {
                    return this.target;
                }
            },
        },
        methods: {
            cancel() {
                this.$refs.popconfirm.$emit('close');
                this.$emit('cancelled');
            },
            confirm() {
                this.$refs.popconfirm.$emit('close');
                this.$emit('confirmed');
            },
        },
    };
</script>
